import { useCallback, useMemo, useState } from 'react';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { Filters } from '../models/Filters';
import { usePackingStations } from './usePackingStations';

export const usePackingStationsMain = () => {
  const { params: queryParams, updateParams: updateQueryParams } = useQueryParams({
    code: { type: 'string', default: '' },
    active: { type: 'boolean', default: undefined as boolean | undefined },
  });

  // Filtros activos (sincronizados con query params)
  const filterCode = queryParams.code ?? '';
  const filterActive = typeof queryParams.active === 'boolean' ? queryParams.active : undefined;

  const [selectedColumns, setSelectedColumns] = useState<string[]>([
    'code',
    'active',
    'printers',
    'clients',
    'locations',
  ]);

  // Construir filtros para la API
  const filters: Filters = useMemo(() => {
    return {
      ...(filterCode.trim() && { code: filterCode.trim() }),
      ...(filterActive !== undefined && { active: filterActive }),
      limit: 50,
    };
  }, [filterCode, filterActive]);

  // Hook de datos
  const {
    packingStations,
    isLoading,
    isFetching,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    total,
  } = usePackingStations(filters);

  // Handlers de filtros
  const handleCodeFilterChange = useCallback(
    (value: string) => {
      updateQueryParams({ code: value });
    },
    [updateQueryParams],
  );

  const handleActiveFilterChange = useCallback(
    (value: boolean | undefined) => {
      const normalizedValue = typeof value === 'boolean' ? value : undefined;
      updateQueryParams({ active: normalizedValue });
    },
    [updateQueryParams],
  );

  const handleColumnsChange = useCallback((newColumns: string[]) => {
    setSelectedColumns(newColumns);
  }, []);

  const clearFilters = useCallback(() => {
    updateQueryParams({
      code: '',
      active: undefined,
    });
  }, [updateQueryParams]);

  // Verificar si hay filtros activos
  const hasActiveFilters = useMemo(() => {
    return Boolean(filterCode.trim()) || filterActive !== undefined;
  }, [filterCode, filterActive]);

  // Props para el componente de tabla
  const tableProps = useMemo(
    () => ({
      packingStations,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
      selectedColumns,
      refetch,
    }),
    [packingStations, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, selectedColumns, refetch],
  );

  return {
    // Estados
    selectedColumns,
    filterCode,
    filterActive,

    // Datos
    packingStations,
    isLoading,
    isFetching,
    error,
    total,

    // Props de tabla
    tableProps,

    // Handlers
    handleCodeFilterChange,
    handleActiveFilterChange,
    handleColumnsChange,
    clearFilters,
    refetch,

    // Infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Estado de filtros
    hasActiveFilters,
  };
};
